<script lang="ts" setup>
import positionUtils from '@zyro-inc/site-modules/utils/positioning';
import { onClickOutside } from '@vueuse/core';
import {
	DATA_ATTRIBUTE_SELECTOR,
	DATA_ATTRIBUTE_SELECTOR_TOOLTIP_CONTENT,
} from '@zyro-inc/site-modules/constants/siteModulesConstants';
import Icon from '@zyro-inc/site-modules/components/Icon.vue';
import type { CSSProperties } from 'vue';
import {
	ref,
	computed,
	onMounted,
	nextTick,
	watch,
} from 'vue';

type Props = {
	position?: 'top' | 'right' | 'bottom' | 'left' | 'rightTop' | 'leftTop';
	size?: 'x-small' | 'small' | 'large' | 'x-large';
	toggleEvent?: 'hover' | 'click' | 'none';
	disabled?: boolean;
	hideTooltip?: boolean;
	contentPosition?: 'absolute' | 'fixed';
	usePortal?: boolean;
	forcedPosition?: boolean | CSSProperties;
	triangle?: boolean;
	arrowOffset?: number;
	isOnModal?: boolean;
	isOnDrawer?: boolean;
	isOnPopup?: boolean;
	mode?: 'light' | 'dark' | 'success';
	hasCloseIcon?: boolean;
	autoClose?: boolean;
	isAdaptivePosition?: boolean;
	customTriangleStyle?: CSSProperties | string;
	closeOnOutsideClick?: boolean;
	builderPreviewContainerRef?: HTMLElement | null;
	maxWidth?: string;
};

const props = withDefaults(defineProps<Props>(), {
	position: 'bottom',
	size: 'large',
	toggleEvent: 'click',
	disabled: false,
	hideTooltip: false,
	contentPosition: 'fixed',
	usePortal: true,
	forcedPosition: false,
	triangle: true,
	arrowOffset: 0,
	isOnModal: false,
	isOnDrawer: false,
	isOnPopup: false,
	mode: 'light',
	hasCloseIcon: false,
	autoClose: false,
	isAdaptivePosition: false,
	closeOnOutsideClick: true,
	builderPreviewContainerRef: null,
});

const emit = defineEmits([
	'trigger-click',
	'on-close',
]);

const TOOLTIP_CLOSE_TIMEOUT = 2000;

const showTooltip = ref(false);
const closeTimeout = ref();
const tooltipPosition = ref({});
const tooltipRef = ref<HTMLElement | null>(null);
const trigger = ref<HTMLElement | null>(null);
const tooltip = ref<HTMLElement | null>(null);

// used for styling
const arrowOffsetValue = computed(() => `${props.arrowOffset}px`);
const maxWidth = computed(() => props.maxWidth || 'unset');

const closeTooltip = () => {
	emit('on-close');
	showTooltip.value = false;
};

const show = computed(() => showTooltip.value && !props.hideTooltip);

const closeIconColor = computed(() => {
	const MODE_COLOR_MAP = {
		light: 'dark',
		dark: 'light',
		success: 'light',
	};

	return MODE_COLOR_MAP[props.mode] || 'dark';
});

const toggleTooltip = () => {
	showTooltip.value = !showTooltip.value;
};

const triggerAction = () => {
	if (props.disabled) return {};
	switch (props.toggleEvent) {
	case 'none': // do nothing
		return {};
	case 'hover': // toggle on 'mouseenteer' / 'mouseleave'
		return {
			mouseenter: toggleTooltip,
			mouseleave: closeTooltip,
		};
	default: // toggle on 'click'
		emit('trigger-click');

		return {
			click: () => {
				toggleTooltip();
				if (show.value && props.autoClose) {
					closeTimeout.value = setTimeout(closeTooltip, TOOLTIP_CLOSE_TIMEOUT);
				}
			},
		};
	}
};

const getAdapativePosition = () => {
	if (!trigger.value) {
		return props.position;
	}

	const triggerRect = trigger.value.getBoundingClientRect();
	const windowWidth = window.innerWidth;
	const windowHeight = window.innerHeight;

	const isTriggerOnRightSide = triggerRect.left > windowWidth / 2;
	const isTriggerOnTopSide = triggerRect.top > windowHeight / 2;

	if ([
		'right',
		'left',
	].includes(props.position)) {
		if (isTriggerOnRightSide) {
			return 'left';
		}

		return 'right';
	}

	if ([
		'top',
		'bottom',
	].includes(props.position)) {
		if (isTriggerOnTopSide) {
			return 'bottom';
		}

		return 'top';
	}

	return props.position;
};

const finalPosition = () => (props.isAdaptivePosition ? getAdapativePosition() : props.position);

const calculatePosition = () => {
	if (!show.value || props.forcedPosition) {
		return;
	}

	const isAbsolute = props.contentPosition === 'absolute';
	// Calculate tooltip position

	switch (finalPosition()) {
	case 'bottom':
		tooltipPosition.value = positionUtils.bottom(trigger.value, tooltip.value, isAbsolute);
		break;
	case 'top':
		tooltipPosition.value = positionUtils.top(
			trigger.value,
			tooltip.value,
			isAbsolute,
			props.builderPreviewContainerRef,
			15,
		);
		break;
	case 'right':
		tooltipPosition.value = positionUtils.right(trigger.value, tooltip.value, isAbsolute);
		break;
	case 'rightTop':
		tooltipPosition.value = positionUtils.rightTop(trigger.value, tooltip.value, isAbsolute);
		break;
	case 'leftTop':
		tooltipPosition.value = positionUtils.leftTop(trigger.value, tooltip.value, isAbsolute);
		break;
	case 'left':
		tooltipPosition.value = positionUtils.left(trigger.value, tooltip.value, isAbsolute);
		break;
		// TODO: extend this with a first need
	default:
		tooltipPosition.value = positionUtils.bottom(trigger.value, tooltip.value, isAbsolute);
	}
};

onClickOutside(tooltipRef, (clickEvent: MouseEvent) => {
	if (
		!(clickEvent.target as Element)?.closest('.tooltip__content')
			&& props.closeOnOutsideClick
			&& !props.hasCloseIcon
			&& showTooltip.value
	) {
		if (closeTimeout.value) {
			clearTimeout(closeTimeout.value);
		}

		closeTooltip();
	}
});

watch(show, async () => {
	await nextTick();
	calculatePosition();
});

onMounted(() => {
	if (props.toggleEvent === 'none') {
		showTooltip.value = true;
	}
});
</script>

<template>
	<div
		ref="tooltipRef"
		class="tooltip"
	>
		<div
			ref="trigger"
			v-qa="'zyro-tooltip-trigger'"
			class="trigger"
			v-on="triggerAction()"
		>
			<slot name="trigger" />
		</div>
		<Teleport
			v-if="show && usePortal"
			to="body"
		>
			<div
				ref="tooltip"
				v-qa="'zyro-tooltip-content'"
				:[DATA_ATTRIBUTE_SELECTOR]="DATA_ATTRIBUTE_SELECTOR_TOOLTIP_CONTENT"
				:class="[
					`tooltip__content
						tooltip__content--${mode}
						tooltip__content--${size}
						tooltip__content--position-${contentPosition}
						body-small`,
					{
						'tooltip__content--modal' : isOnModal,
						'tooltip__content--drawer' : isOnDrawer,
						'tooltip__content--popup' : isOnPopup,
						'tooltip__content--has-close-icon' : hasCloseIcon
					}
				]"
				:style="forcedPosition || tooltipPosition"
			>
				<div
					v-show="triangle"
					class="tooltip__triangle"
					:class="[
						`tooltip__triangle
							tooltip__triangle--${mode}`,
						{
							'tooltip__triangle--top': finalPosition() === 'bottom',
							'tooltip__triangle--bottom': finalPosition() === 'top',
							'tooltip__triangle--left': finalPosition() === 'right',
							'tooltip__triangle--right': finalPosition() === 'left'
						}
					]"
					:style="customTriangleStyle"
				/>
				<slot />
				<div v-if="hasCloseIcon">
					<Icon
						v-qa="'tooltip-btn-close'"
						name="close"
						:width="16"
						:height="16"
						:color="closeIconColor"
						:class="
							`tooltip__close-icon
							tooltip__close-icon--${size}
							tooltip__close-icon--position-${contentPosition}`"
						@click="closeTooltip"
					/>
				</div>
			</div>
		</Teleport>
		<div
			v-else-if="show && !usePortal"
			ref="tooltip"
			v-qa="'zyro-tooltip-content'"
			:[DATA_ATTRIBUTE_SELECTOR]="DATA_ATTRIBUTE_SELECTOR_TOOLTIP_CONTENT"
			:class="[
				`tooltip__content
					tooltip__content--${mode}
					tooltip__content--${size}
					tooltip__content--position-${contentPosition}
					body-small`,
				{
					'tooltip__content--modal' : isOnModal,
					'tooltip__content--drawer' : isOnDrawer,
					'tooltip__content--popup' : isOnPopup,
					'tooltip__content--has-close-icon' : hasCloseIcon
				}
			]"
			:style="forcedPosition || tooltipPosition"
		>
			<div
				v-show="triangle"
				class="tooltip__triangle"
				:class="[
					`tooltip__triangle
						tooltip__triangle--${mode}`,
					{
						'tooltip__triangle--top': finalPosition() === 'bottom',
						'tooltip__triangle--bottom': finalPosition() === 'top',
						'tooltip__triangle--left': finalPosition() === 'right',
						'tooltip__triangle--right': finalPosition() === 'left'
					}
				]"
				:style="customTriangleStyle"
			/>
			<slot />
			<div v-if="hasCloseIcon">
				<Icon
					v-qa="'tooltip-btn-close'"
					name="close"
					:width="16"
					:height="16"
					:color="closeIconColor"
					:class="
						`tooltip__close-icon
						tooltip__close-icon--${size}
						tooltip__close-icon--position-${contentPosition}`"
					@click="closeTooltip"
				/>
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
.tooltip {
	$this: &;

	position: relative;

	&__content {
		z-index: $z-index-controls-tooltip;
		border-radius: 5px;
		box-shadow: $box-shadow;
		max-width: v-bind('maxWidth');

		&--dark {
			color: $color-light;
			background: $color-dark;

			#{$this}__triangle {
				background: $color-dark;
			}
		}

		&--success {
			color: $color-light;
			background: $color-success;

			#{$this}__triangle {
				background: $color-success;
			}
		}

		&--light {
			color: $color-dark;
			background: $color-light;

			#{$this}__triangle {
				background: $color-light;
			}
		}

		&--modal {
			z-index: $z-index-modal-controls;
		}

		&--drawer {
			z-index: $z-index-layout-drawer;
		}

		&--popup {
			z-index: $z-index-popup;
		}

		&--x-small {
			padding: 0;
		}

		&--small {
			padding: 8px 16px;
		}

		&--large {
			padding: 12px 16px;
		}

		&--x-large {
			padding: 18px 24px;
		}

		&--position {
			top: 0;
			left: 0;

			&-absolute {
				position: absolute;
			}

			&-fixed {
				position: fixed;
			}
		}

		&--has-close-icon {
			padding-right: 32px;
		}
	}

	&__triangle {
		position: absolute;
		width: 8px;
		height: 8px;
		margin: auto;
		content: "";

		&--top {
			top: -4px;
			right: 0;
			left: 0;
			transform: translateX(v-bind('arrowOffsetValue')) rotate(45deg);
		}

		&--bottom {
			right: 0;
			bottom: -4px;
			left: 0;
			transform: translateX(v-bind('arrowOffsetValue')) rotate(45deg);
		}

		&--left {
			top: 0;
			bottom: 0;
			left: -4px;
			transform: translateY(v-bind('arrowOffsetValue')) rotate(45deg);
		}

		&--right {
			top: 0;
			right: -4px;
			bottom: 0;
			transform: translateY(v-bind('arrowOffsetValue')) rotate(45deg);
		}

		// TODO: handle other positions with a first need
	}

	&__close-icon {
		position: absolute;
		top: 0;
		right: 8px;
		cursor: pointer;

		&--x-small {
			top: 2px;
		}

		&--small {
			top: 10px;
		}

		&--large {
			top: 14px;
		}

		&--x-large {
			top: 20px;
		}
	}
}

.trigger {
	display: flex;
	align-items: center;
	justify-content: center;
}
</style>
